import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { ILanguage } from 'core/interface/language';
import DefaultImage from 'components/DefaultImage/Index';
import Footer from 'components/Footer/Index';
import Navbar from 'components/Navbar';
import { SwitcherEnum } from 'core/enum/switcherEnum';
import { ISocialMediaData } from 'core/interface/socialMedia';
import CompetencyPreferencesTab from 'components/Tabs/CompetencyPreferences/Index';
import EducationExperienceTab from 'components/Tabs/EducationAndExperience/Index';
import ImageAndYoutubeTab from 'components/Tabs/ImageAndYoutube/Index';
import SkillsTab from 'components/Tabs/Skills/Index';
import { headerItemsProfile } from 'core/constants/header';
import { ProfileTabEnum } from 'core/enum/dashboardTab';
import { IHeaderItems } from 'core/interface/headerItems';
import useLocales from 'hooks/useLocales';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { RootState } from 'redux/store';
import { CandidateService } from 'services/candidate/candidate';
import Image from 'components/image/Image';
import Button from 'components/Button/Index';
import SkeletonLoader from 'components/shared/Skeleton/Index';
import ProfileAbout from 'components/shared/profileTabs/About/Index';
import Popup from 'components/shared/Popups/Popup/Index';
import DownloadPDFPopup from 'components/shared/Popups/DownloadPDF/Index';
import CircularProgress from '@mui/material/CircularProgress';
import { CANDIDATE_ROUTES } from 'core/constants/candidateRoutes';

import './Index.scss';
import colors from 'assets/variables/colors';

const ViewProfile: FC<ILanguage> = ({ params }): JSX.Element => {
  const navigate = useNavigate();
  const { t } = useLocales();
  const { id, tab } = useParams();

  const user = useSelector((state: RootState) => state.user.user);
  const userLoading = useSelector((state: RootState) => state.user.userLoading);
  const [showDownload, setShowDownload] = useState(false);
  const { socialMedia } = useSelector((state: RootState) => state.socialMedia);
  const [candidate, setCandidate] = useState<any>();
  const [tabHeader, setTabHeader] = useState<IHeaderItems[]>(
    headerItemsProfile(t)
  );
  const [tabName, setTabName] = useState<string>(ProfileTabEnum.ABOUT_ME);
  const uploadFile = useRef<HTMLInputElement>(null);
  const [uploadLoading, setUploadLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(3);
  const tabSlugToEnum = useMemo(
    () => ({
      [CANDIDATE_ROUTES.PROFILE_TAB_ABOUT_ME]: ProfileTabEnum.ABOUT_ME,
      [CANDIDATE_ROUTES.PROFILE_TAB_TALENT_PROFILE]:
        ProfileTabEnum.COMPETENCY_SCORES,
      [CANDIDATE_ROUTES.PROFILE_TAB_SKILLS]: ProfileTabEnum.SKILLS,
      [CANDIDATE_ROUTES.PROFILE_TAB_CAREER_SCHOOL]:
        ProfileTabEnum.EDUCATION_EXPERIENCE,
      [CANDIDATE_ROUTES.PROFILE_TAB_PERSONAL_BRANDING]:
        ProfileTabEnum.IMAGE_YOUTUBE
    }),
    []
  );
  const tabEnumToSlug = useMemo(
    () => ({
      [ProfileTabEnum.ABOUT_ME]: CANDIDATE_ROUTES.PROFILE_TAB_ABOUT_ME,
      [ProfileTabEnum.COMPETENCY_SCORES]:
        CANDIDATE_ROUTES.PROFILE_TAB_TALENT_PROFILE,
      [ProfileTabEnum.SKILLS]: CANDIDATE_ROUTES.PROFILE_TAB_SKILLS,
      [ProfileTabEnum.EDUCATION_EXPERIENCE]:
        CANDIDATE_ROUTES.PROFILE_TAB_CAREER_SCHOOL,
      [ProfileTabEnum.IMAGE_YOUTUBE]:
        CANDIDATE_ROUTES.PROFILE_TAB_PERSONAL_BRANDING
    }),
    []
  );

  useEffect(() => {
    if (!userLoading) {
      getCandidateById();
    }
    // eslint-disable-next-line
  }, [id, userLoading]);

  useEffect(() => {
    if (error === 'You are not authorized to view this profile.') {
      setCountdown(3);
      const timer = setTimeout(() => {
        navigate(-1);
      }, 3000);
      const interval = setInterval(() => {
        setCountdown((prev) => (prev > 1 ? prev - 1 : 1));
      }, 1000);
      return () => {
        clearTimeout(timer);
        clearInterval(interval);
      };
    }
  }, [error, navigate]);

  const getCandidateById = async () => {
    setLoading(true);
    setError(null);
    try {
      const candidateData = await CandidateService.getCandidate(id ? +id : 0);
      setCandidate(candidateData);
      // Permission check after fetching candidate
      if (!user) {
        setError('You are not authorized to view this profile.');
      } else if (user.role === 'candidate') {
        // Only allow if this is the candidate themselves
        if (user.id !== candidateData?.user) {
          setError('You are not authorized to view this profile.');
        }
      } else if (user.role === 'recruiter' || user.role === 'admin') {
        if (!user.can_view_candidates.includes(Number(id))) {
          setError('You are not authorized to view this profile.');
        }
      }
    } catch (err: any) {
      setError('An error occurred while loading the profile.');
    } finally {
      setLoading(false);
    }
  };

  const updateCandidate = async (isAnonym: number): Promise<void> => {
    const body = {
      if_seachable: isAnonym === SwitcherEnum.SHOW_ME
    };
    if (candidate && candidate.id) {
      CandidateService.updateCandidate(body, candidate.id);
    }
  };

  const handleTabChange = (clickedItem: IHeaderItems): void => {
    setTabHeader((prevValues) =>
      prevValues.map((item) =>
        item === clickedItem
          ? { ...item, isActive: true }
          : { ...item, isActive: false }
      )
    );
    if (clickedItem.value) {
      setTabName(clickedItem.value);
      if (id) {
        const nextSlug = tabEnumToSlug[clickedItem.value as ProfileTabEnum];
        if (nextSlug) {
          navigate(CANDIDATE_ROUTES.profileTab(id, nextSlug), { replace: true });
        }
      }
    }
  };

  useEffect(() => {
    const activeTab =
      tabSlugToEnum[tab as keyof typeof tabSlugToEnum] || ProfileTabEnum.ABOUT_ME;

    setTabName(activeTab);
    setTabHeader((prevValues) =>
      prevValues.map((item) => ({
        ...item,
        isActive: item.value === activeTab
      }))
    );
  }, [tab, tabSlugToEnum]);

  const handleSwitchChange = (e: SwitcherEnum): void => {
    updateCandidate(e);
  };

  const showSelectedTab = (): JSX.Element | null => {
    switch (tabName) {
      case ProfileTabEnum.ABOUT_ME:
        return (
          <ProfileAbout t={t} candidate_id={candidate?.id} fromGuest noMargin />
        );

      case ProfileTabEnum.COMPETENCY_SCORES:
        return <CompetencyPreferencesTab t={t} fromGuest />;
      case ProfileTabEnum.SKILLS:
        return <SkillsTab t={t} candidate_id={candidate?.id} fromGuest />;
      case ProfileTabEnum.IMAGE_YOUTUBE:
        return (
          <ImageAndYoutubeTab t={t} fromGuest candidate_id={candidate?.id} />
        );

      case ProfileTabEnum.EDUCATION_EXPERIENCE:
        return (
          <EducationExperienceTab
            t={t}
            candidate_id={candidate?.id}
            fromGuest
          />
        );

      default:
        return <ProfileAbout t={t} candidate_id={candidate?.id} fromGuest />;
    }
  };

  const handleBackClick = (): void => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    if (id) {
      navigate(CANDIDATE_ROUTES.profileTab(id, CANDIDATE_ROUTES.PROFILE_TAB_ABOUT_ME));
    }
  };

  if (userLoading) {
    return (
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  if (loading) return <div>Loading...</div>;
  if (error)
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh'
        }}
      >
        <div
          style={{
            background: '#fff',
            borderRadius: '16px',
            boxShadow: '0 4px 24px rgba(82, 37, 131, 0.10)',
            padding: '40px 32px',
            maxWidth: '400px',
            textAlign: 'center',
            border: '1px solid #E5E5E5'
          }}
        >
          <h2 style={{ color: colors.tenantColor, marginBottom: 16 }}>
            {t('Access Denied')}
          </h2>
          <div style={{ color: '#E55353', fontSize: 18, marginBottom: 12 }}>
            {error}
          </div>
          <div style={{ color: '#333', fontSize: 16 }}>
            {t('You will be redirected back in')} <b>{countdown}</b> second
            {countdown !== 1 ? 's' : ''}.
          </div>
        </div>
      </div>
    );

  return (
    <div className="view-profile-wrapper">
      <Navbar />
      <div className="dashboard">
        <div onClick={handleBackClick} className="back-button skillio-back-button">
          <Image name="back.svg" width={16} height={16} />
          <span>{t('Back')}</span>
        </div>
        <div className="dashboard__main">
          <div className="dashboard__left">
            <div className="dashboard__left__image">
              {!candidate ? (
                <SkeletonLoader />
              ) : (
                <>
                  {candidate && candidate.avatar ? (
                    <Image
                      className="dashboard__left__image-img"
                      name="profile-image.png"
                      width={236}
                      height={236}
                      url={candidate?.avatar}
                      objectFit="cover"
                    />
                  ) : (
                    <DefaultImage
                      width={236}
                      height={236}
                      className="dashboard__left__image-img"
                      letter={candidate?.name}
                    />
                  )}
                </>
              )}
            </div>
            <div className="dashboard__left__content">
              <div className="dashboard__left__contact">
                <div className="dashboard__left__contact__user">
                  <div className="dashboard__left__contact__user__icon">
                    <Image
                      className="dashboard__left__contact__user__icon-img"
                      name="email.svg"
                      width={12}
                      height={12}
                    />
                  </div>
                  <span className="dashboard__left__contact__user-contact">
                    {candidate?.email}
                  </span>
                </div>
                {candidate?.phone && (
                  <div className="dashboard__left__contact__user">
                    <div className="dashboard__left__contact__user__icon">
                      <Image
                        className="dashboard__left__contact__user__icon-img"
                        name="phone.svg"
                        width={12}
                        height={12}
                      />
                    </div>
                    <span className="dashboard__left__contact__user-contact">
                      {candidate.phone}
                    </span>
                  </div>
                )}
              </div>
              <div className="dashboard__left__social">
                {socialMedia &&
                  socialMedia.map((item: ISocialMediaData) => (
                    <a
                      className="disable-block"
                      href={item.url}
                      key={item.id}
                      target="blank"
                    >
                      <Image
                        key={item.id}
                        className="dashboard__left__social-icons"
                        width={31}
                        height={31}
                        name={`${item.network}.svg`}
                      />
                    </a>
                  ))}
              </div>
              <div className="dashboard__left__actions">
                <Button
                  isLoading={uploadLoading}
                  onClick={() => {
                    if (candidate?.report_urls?.mo_de) {
                      window.open(candidate?.report_urls?.mo_de, 'blank');
                    }
                  }}
                  t={t}
                  className="dashboard__left__actions-lightGreen disable-block"
                  title={t('Motivator Demotivator Report')}
                />

                <Button
                  onClick={() => {
                    if (candidate?.cv) window.open(candidate?.cv, '_blank');
                  }}
                  t={t}
                  iconName="viewCv.svg"
                  className="dashboard__left__actions-purple disable-block"
                  title={t('View CV')}
                  disabled={!candidate?.cv}
                  tooltip={
                    !candidate?.cv
                      ? 'The candidate has not uploaded a CV'
                      : undefined
                  }
                />

                <Button
                  disabled={uploadLoading || !candidate}
                  onClick={() => setShowDownload(true)}
                  t={t}
                  iconName="resume.svg"
                  className="dashboard__left__actions-sky disable-block"
                  title={t('recrout_resume')}
                  tooltip={
                    !candidate
                      ? 'Candidate profile data is required to download resume'
                      : undefined
                  }
                />

                <Button
                  t={t}
                  onClick={() => {
                    if (candidate?.report_urls?.feedback) {
                      window.open(candidate?.report_urls?.feedback, 'blank');
                    }
                  }}
                  iconName="report.svg"
                  className="dashboard__left__actions-green disable-block"
                  title={t('Candidate Report')}
                />

                {/* <div className="otherColorButton">
                  <Button
                    t={t}
                    className="dashboard__left__actions-lightPurple disable-block"
                    title={t('Send to ATS/HRIS')}
                  />
                </div> */}
              </div>
            </div>
          </div>
          <div className="dashboard__right">
            <div className="dashboard__right__bottom">
              <div className="dashboard__right__bottom__tabs">
                <div className="dashboard__right__bottom__tabs__header">
                  {tabHeader.map((item, index) => (
                    <div key={index}>
                      <div className="dashboard__right__bottom__tabs__header__item disable-block">
                        <span
                          onClick={() => handleTabChange(item)}
                          className={`dashboard__right__bottom__tabs__header__item-title ${
                            item.isActive ? 'active' : ''
                          }`}
                        >
                          {t(item.title)}
                          <span className="line"></span>
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="dashboard__right__bottom-tab">
                {showSelectedTab()}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
      {showDownload && (
        <Popup
          title={t('Download PDF')}
          description={t('Please select the file you would like to download')}
          onClose={() => setShowDownload(false)}
        >
          <DownloadPDFPopup setIsClose={() => setShowDownload(false)} />
        </Popup>
      )}
    </div>
  );
};

export default ViewProfile;
